import { createFileRoute } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { FeatureGate } from "@/components/FeatureGate";
import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Edit3, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTrialLimits, type ClientAccount } from "@/hooks/useTrialLimits";

export const Route = createFileRoute("/_app/clients")({
  component: () => (
    <FeatureGate feature="clients">
      <ClientsPage />
    </FeatureGate>
  ),
  head: () => ({
    meta: [
      { title: "Clients · AdKavi" },
      { name: "description", content: "Manage client accounts for your agency." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const PRESET_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e",
  "#f97316", "#eab308", "#10b981", "#06b6d4",
];

function ClientsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { clients, loading, activeClientId, setActiveClientId, refreshClients } = useTrialLimits();
  const [editing, setEditing] = useState<ClientAccount | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  const openNew = () => {
    setEditing(null);
    setShowDialog(true);
  };

  const openEdit = (c: ClientAccount) => {
    setEditing(c);
    setShowDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("clients.deleteConfirm"))) return;
    const { error } = await supabase.from("client_accounts").delete().eq("id", id);
    if (error) {
      toast.error(t("clients.toast.deleteFailed"), { description: error.message });
      return;
    }
    toast.success(t("clients.toast.removed"));
    if (activeClientId === id) setActiveClientId(null);
    refreshClients();
  };

  return (
    <section className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("clients.heading")}</span>
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t("clients.desc")}
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-1.5" /> {t("clients.addBtn")}
        </Button>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-border/50 bg-card/40 p-10 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t("clients.loading")}
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold">{t("clients.empty.title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              {t("clients.empty.desc")}
            </p>
            <Button className="mt-5" onClick={openNew}>
              <Plus className="w-4 h-4 mr-1.5" /> {t("clients.addBtn")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {clients.map((c) => {
            const isActive = c.id === activeClientId;
            return (
              <Card key={c.id} className={isActive ? "border-primary/60" : undefined}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-10 h-10 rounded-lg shrink-0"
                      style={{ backgroundColor: c.brand_color ?? "hsl(var(--primary))" }}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{c.name}</CardTitle>
                      <CardDescription>
                        {isActive ? (
                          <span className="text-primary inline-flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" /> {t("clients.activeBadge")}
                          </span>
                        ) : (
                          t("clients.activateHint")
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {!isActive && (
                    <Button size="sm" variant="default" onClick={() => setActiveClientId(c.id)}>
                      {t("clients.activateBtn")}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                    <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {t("clients.editBtn")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("clients.deleteBtn")}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ClientDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        userId={user?.id}
        editing={editing}
        onSaved={(saved, isNew) => {
          refreshClients();
          if (isNew) setActiveClientId(saved.id);
        }}
      />
    </section>
  );
}

function ClientDialog({
  open,
  onOpenChange,
  userId,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string | undefined;
  editing: ClientAccount | null;
  onSaved: (saved: ClientAccount, isNew: boolean) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [saving, setSaving] = useState(false);

  // Sync when opening or switching the editing target
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setColor(editing.brand_color ?? PRESET_COLORS[0]);
    } else {
      setName("");
      setColor(PRESET_COLORS[0]);
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!userId) return;
    if (!name.trim()) {
      toast.error(t("clients.toast.addName"));
      return;
    }
    setSaving(true);
    if (editing) {
      const { data, error } = await supabase
        .from("client_accounts")
        .update({ name: name.trim(), brand_color: color })
        .eq("id", editing.id)
        .select("id, name, brand_color")
        .single();
      setSaving(false);
      if (error || !data) {
        toast.error(t("clients.toast.saveFailed"), { description: error?.message });
        return;
      }
      onSaved(data as ClientAccount, false);
      toast.success(t("clients.toast.updated"));
    } else {
      const { data, error } = await supabase
        .from("client_accounts")
        .insert({ user_id: userId, name: name.trim(), brand_color: color })
        .select("id, name, brand_color")
        .single();
      setSaving(false);
      if (error || !data) {
        toast.error(t("clients.toast.addFailed"), { description: error?.message });
        return;
      }
      onSaved(data as ClientAccount, true);
      toast.success(t("clients.toast.added"));
    }
    setName("");
    setColor(PRESET_COLORS[0]);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setName("");
          setColor(PRESET_COLORS[0]);
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? t("clients.dialog.editTitle") : t("clients.dialog.addTitle")}</DialogTitle>
          <DialogDescription>
            {t("clients.dialog.desc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="client-name">{t("clients.dialog.nameLabel")}</Label>
            <Input id="client-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>{t("clients.dialog.colorLabel")}</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-md border-2 ${color === c ? "border-foreground" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("clients.dialog.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("clients.dialog.saving")}</> : editing ? t("clients.dialog.saveBtn") : t("clients.dialog.addBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
